import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, Button, Table, Tag, Tooltip, Dropdown, Modal, Spin, Empty, Row, Col } from 'antd';
import {
  SearchOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  AppstoreOutlined,
  BarsOutlined,
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { educationAssetService, userService } from '@iso/services';
import AssetModal from './AssetModal';
import TrainingAssetDetail from './TrainingAssetDetail';

const { Option } = Select;

const CATEGORIES = [
  { value: 'induction', label: 'Induction' },
  { value: 'safety', label: 'Safety' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'technical', label: 'Technical' },
  { value: 'whs', label: 'WHS' },
  { value: 'other', label: 'Other' },
];

const TrainingAssets = ({ propertyId, onRunSession }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);
  const [userList, setUserList] = useState([]);

  useEffect(() => {
    if (!propertyId) return;
    fetchAssets();
    fetchUsers();
  }, [propertyId, currentPage, search, filterType, filterCategory, filterStatus]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const res = await educationAssetService.getList(propertyId, {
        page: currentPage,
        search,
        type: filterType,
        category: filterCategory,
        status: filterStatus,
      });
      if (res) {
        setAssets(res.education_assets || []);
        setTotalItems(res.total_item || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await userService.getUserForProperty(propertyId);
      if (res && res.code === '0000') setUserList(res.users || []);
    } catch (e) { console.error('Failed to fetch users:', e); }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Training Asset',
      content: 'Are you sure you want to delete this training asset? This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        await educationAssetService.destroy(id);
        fetchAssets();
      },
    });
  };

  const handleSaved = () => {
    setModalVisible(false);
    setEditingAsset(null);
    fetchAssets();
  };

  const getStatusTag = (status) => {
    const color = status === 'active' ? 'success' : 'default';
    return <Tag color={color}>{status}</Tag>;
  };

  const getCategoryTag = (category) => {
    if (!category) return null;
    return <Tag>{category}</Tag>;
  };

  const getActionsMenuProps = (asset) => ({
    items: [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Edit',
        onClick: ({ domEvent }) => { domEvent.stopPropagation(); setEditingAsset(asset); setModalVisible(true); },
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete',
        danger: true,
        onClick: ({ domEvent }) => { domEvent.stopPropagation(); handleDelete(asset.id); },
      },
    ],
  });

  const tableColumns = [
    {
      title: 'Asset Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a style={{ color: '#0B2A1B', fontWeight: 600 }} onClick={() => setDetailAsset(record)}>{text}</a>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: t => <Tag color="blue">{t}</Tag>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: getCategoryTag,
    },
    {
      title: 'Validity',
      dataIndex: 'validity_months',
      key: 'validity_months',
      render: v => v ? `${v} months` : '—',
    },
    {
      title: 'Trainer',
      dataIndex: 'primary_trainer_name',
      key: 'trainer',
      render: t => t || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: 'Contractors',
      dataIndex: 'available_to_contractors',
      key: 'available_to_contractors',
      render: v => v ? <Tag className="contractor-badge">Available</Tag> : '—',
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_, record) => (
        <Dropdown menu={getActionsMenuProps(record)} trigger={['click']}>
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  if (detailAsset) {
    return (
      <TrainingAssetDetail
        asset={detailAsset}
        propertyId={propertyId}
        onBack={() => setDetailAsset(null)}
        onRunSession={onRunSession}
      />
    );
  }

  return (
    <div className="tab-content">
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div className="filter-row">
          <Input
            className="search-bar training-search-bar"
            prefix={<SearchOutlined />}
            suffix={search ? <CloseCircleOutlined onClick={() => setSearch('')} /> : null}
            placeholder="Search assets..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <Select
            className="filter-select"
            value={filterType}
            onChange={v => { setFilterType(v); setCurrentPage(1); }}
            placeholder="All Types"
          >
            <Option value="">All Types</Option>
            <Option value="course">Course</Option>
            <Option value="training">Training</Option>
          </Select>
          <Select
            className="filter-select"
            value={filterCategory}
            onChange={v => { setFilterCategory(v); setCurrentPage(1); }}
            placeholder="All Categories"
          >
            <Option value="">All Categories</Option>
            {CATEGORIES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
          </Select>
          <Select
            className="filter-select"
            value={filterStatus}
            onChange={v => { setFilterStatus(v); setCurrentPage(1); }}
            placeholder="All Statuses"
          >
            <Option value="">All Statuses</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Tooltip title="Grid view">
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode('grid')}
              style={viewMode === 'grid' ? { background: '#0B2A1B', borderColor: '#0B2A1B' } : {}}
            />
          </Tooltip>
          <Tooltip title="List view">
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<BarsOutlined />}
              onClick={() => setViewMode('list')}
              style={viewMode === 'list' ? { background: '#0B2A1B', borderColor: '#0B2A1B' } : {}}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: '#0B2A1B', borderColor: '#0B2A1B', borderRadius: 8 }}
            onClick={() => { setEditingAsset(null); setModalVisible(true); }}
          >
            Add Training Asset
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : assets.length === 0 ? (
        <Empty description="No training assets found" style={{ marginTop: 60 }} />
      ) : viewMode === 'grid' ? (
        <div className="asset-grid">
          {assets.map(asset => (
            <div key={asset.id} className="asset-card" onClick={() => setDetailAsset(asset)}>
              <div className="asset-name">{asset.name}</div>
              <div className="asset-desc">{asset.description || 'No description'}</div>
              <div className="asset-meta">
                {asset.type && <Tag color="blue">{asset.type}</Tag>}
                {asset.category && <Tag>{asset.category}</Tag>}
                {asset.status && getStatusTag(asset.status)}
              </div>
              <div className="asset-footer">
                <span>
                  {asset.validity_months ? `${asset.validity_months} month validity` : 'No expiry'}
                </span>
                <div
                  style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                  onClick={e => e.stopPropagation()}
                >
                  {asset.available_to_contractors ? <Tag className="contractor-badge">Contractors</Tag> : null}
                  <Dropdown menu={getActionsMenuProps(asset)} trigger={['click']}>
                    <Button type="text" icon={<EllipsisOutlined />} size="small" />
                  </Dropdown>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table
          dataSource={assets}
          columns={tableColumns}
          rowKey="id"
          pagination={{
            current: currentPage,
            total: totalItems,
            onChange: setCurrentPage,
            showSizeChanger: false,
          }}
          scroll={{ x: 800 }}
        />
      )}

      <AssetModal
        visible={modalVisible}
        asset={editingAsset}
        propertyId={propertyId}
        userList={userList}
        onClose={() => { setModalVisible(false); setEditingAsset(null); }}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default TrainingAssets;
